<script setup>
// NOTICE: KAPTION-INTEGRATION — Time-series stacked bar chart for campaign delivery.
// Wraps the shared BarChart (chart.js). One stacked bar per time bucket, segmented
// by delivery status. Data is expected PRE-AGGREGATED from the wanalytics API
// (Kaption::AnalyticsDataService) — this component does no aggregation, only display.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BarChart from 'shared/components/charts/BarChart.vue';

const props = defineProps({
  // Array of { date, sent, delivered, read, replied, failed }
  data: {
    type: Array,
    default: () => [],
  },
  granularity: {
    type: String,
    default: 'day',
    validator: v => ['day', 'week', 'month'].includes(v),
  },
});

const { t } = useI18n();

// Status colours mirror the sibling Kaption report components
// (slate / teal / green / ruby Radix scale) so the dashboard reads consistently.
const STATUS_SERIES = [
  {
    key: 'delivered',
    labelKey: 'CAMPAIGN.REPORT.METRICS.DELIVERED',
    color: '#83cdc1',
  }, // teal-7
  { key: 'read', labelKey: 'CAMPAIGN.REPORT.METRICS.READ', color: '#12a594' }, // teal-9
  // NOTICE: KAPTION-INTEGRATION — clicks series (green-10 for contrast vs replied)
  {
    key: 'clicked',
    labelKey: 'CAMPAIGN.REPORT.METRICS.CLICKS',
    color: '#2b9a66', // green-10
  },
  {
    key: 'replied',
    labelKey: 'CAMPAIGN.REPORT.METRICS.REPLIED',
    color: '#30a46c',
  }, // green-9
  {
    key: 'failed',
    labelKey: 'CAMPAIGN.REPORT.METRICS.FAILED',
    color: '#e54666',
  }, // ruby-9
];

const formatLabel = dateStr => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  if (props.granularity === 'month') {
    return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
  }
  if (props.granularity === 'week') {
    return `${t('CAMPAIGN.REPORT.CHART.WEEK_PREFIX')} ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const collection = computed(() => ({
  labels: props.data.map(point => formatLabel(point.date)),
  datasets: STATUS_SERIES.map(series => ({
    label: t(series.labelKey),
    data: props.data.map(point => point[series.key] ?? 0),
    backgroundColor: series.color,
    borderRadius: 2,
    maxBarThickness: 48,
  })),
}));

const fontFamily =
  'Inter,-apple-system,system-ui,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif';

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    // Legend is rendered as HTML chips in the header (more reliable than the
    // chart.js canvas legend at small chart heights).
    legend: { display: false },
    title: { display: false },
    tooltip: {
      enabled: true,
      mode: 'index',
      intersect: false,
    },
  },
  scales: {
    x: {
      stacked: true,
      grid: { display: false, drawOnChartArea: false },
      ticks: { font: { family: fontFamily, size: 11 }, color: '#8b8d98' },
    },
    y: {
      stacked: true,
      beginAtZero: true,
      grid: { color: 'rgba(139,141,152,0.12)', drawOnChartArea: true },
      border: { display: false },
      ticks: { font: { family: fontFamily, size: 11 }, color: '#8b8d98' },
    },
  },
}));

const hasData = computed(() => props.data && props.data.length > 0);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center justify-between flex-wrap gap-2">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('CAMPAIGN.REPORT.CHART.DELIVERY_OVER_TIME') }}
      </h3>
      <div v-if="hasData" class="flex items-center gap-3 flex-wrap">
        <span
          v-for="series in STATUS_SERIES"
          :key="series.key"
          class="flex items-center gap-1.5 text-xs text-n-slate-11"
        >
          <span
            class="w-2.5 h-2.5 rounded-sm"
            :style="{ backgroundColor: series.color }"
          />
          {{ t(series.labelKey) }}
        </span>
      </div>
    </div>

    <div
      v-if="hasData"
      class="rounded-xl border border-n-weak bg-n-alpha-black2 p-4"
    >
      <div class="h-64">
        <BarChart :collection="collection" :chart-options="chartOptions" />
      </div>
    </div>

    <div
      v-else
      class="flex flex-col items-center justify-center py-12 gap-2 rounded-xl border border-n-weak bg-n-alpha-black2"
    >
      <span class="i-lucide-bar-chart-3 w-8 h-8 text-n-slate-8" />
      <span class="text-sm text-n-slate-10">
        {{ t('CAMPAIGN.REPORT.CHART.NO_DATA') }}
      </span>
    </div>
  </div>
</template>
